import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Globe, TrendingUp, MessageCircle, Map as MapIcon } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import LeadCapture from '../components/LeadCapture';
import PopularArticles from '../components/PopularArticles';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, t } = useLanguage();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/xml/featured?limit=6&lang=${language}`)
      .then(res => res.json())
      .then(data => {
        setFeaturedProperties(data.slice(0, 6));
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [language]);

  return (
    <div style={styles.page}>
      <SEO titleKey="seo.homeTitle" descriptionKey="seo.homeDescription" />
      {/* Hero Section */}
      <section style={styles.hero} data-testid="hero-section">
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle} data-testid="hero-title">
            {t('home.heroTitle')}
          </h1>
          <p style={styles.heroSubtitle} data-testid="hero-subtitle">
            {t('home.heroSubtitle')}
          </p>
          <div style={styles.heroCTAs}>
            <Link to="/woningen" style={styles.btnPrimary} data-testid="hero-cta-primary">
              {t('home.viewProperties')}
              <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
            </Link>
            <Link to="/kaart" style={styles.btnSecondary} data-testid="hero-cta-map">
              <MapIcon size={18} style={{ marginRight: '0.5rem' }} />
              {t('home.searchOnMap')}
            </Link>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.uspGrid}>
            <div style={styles.uspCard} data-testid="usp-card-selection">
              <Award size={40} color="#C5A059" />
              <h3 style={styles.uspTitle}>{t('home.uspSelection')}</h3>
              <p style={styles.uspText}>{t('home.uspSelectionText')}</p>
            </div>
            <div style={styles.uspCard} data-testid="usp-card-expertise">
              <Globe size={40} color="#C5A059" />
              <h3 style={styles.uspTitle}>{t('home.uspExpertise')}</h3>
              <p style={styles.uspText}>{t('home.uspExpertiseText')}</p>
            </div>
            <div style={styles.uspCard} data-testid="usp-card-investment">
              <TrendingUp size={40} color="#C5A059" />
              <h3 style={styles.uspTitle}>{t('home.uspInvestment')}</h3>
              <p style={styles.uspText}>{t('home.uspInvestmentText')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle} data-testid="featured-properties-title">
              {t('home.featuredProperties')}
            </h2>
            <p style={styles.sectionSubtitle}>{t('home.featuredSubtitle')}</p>
          </div>
          
          {loading ? (
            <p style={styles.loadingText}>{t('common.loading')}</p>
          ) : featuredProperties.length > 0 ? (
            <div style={styles.propertiesGrid}>
              {featuredProperties.map((property, idx) => (
                <PropertyCard key={property.id} property={property} priority={idx < 3} />
              ))}
            </div>
          ) : (
            <p style={styles.emptyText}>No featured properties available.</p>
          )}

          <div style={styles.ctaCenter}>
            <Link to="/woningen" style={styles.btnPrimary} data-testid="view-all-properties-button">
              {t('home.viewAllProperties')}
              <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
            </Link>
          </div>
        </div>
      </section>

      <LeadCapture />

      {/* Popular articles from the Knowledge Base — social proof + SEO
          crosslinking into deep content. Renders nothing when views are all
          zero to keep the landing tight. */}
      <PopularArticles variant="home" limit={3} />

      {/* Regions Section */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle} data-testid="regions-title">{t('home.discoverRegions')}</h2>
            <p style={styles.sectionSubtitle}>{t('home.regionsSubtitle')}</p>
          </div>

          <div style={styles.regionsGrid}>
            <Link to="/costa-blanca" style={styles.regionCard} data-testid="region-card-costa-blanca">
              <div style={styles.regionOverlay}>
                <h3 style={styles.regionTitle}>Costa Blanca</h3>
                <p style={styles.regionSubtitle}>{t('home.costaBlancaSubtitle')}</p>
              </div>
            </Link>
            <Link to="/costa-calida" style={styles.regionCard} data-testid="region-card-costa-calida">
              <div style={styles.regionOverlay}>
                <h3 style={styles.regionTitle}>Costa Calida</h3>
                <p style={styles.regionSubtitle}>{t('home.costaCalidaSubtitle')}</p>
              </div>
            </Link>
            <Link to="/costa-almeria" style={styles.regionCard} data-testid="region-card-costa-almeria">
              <div style={styles.regionOverlay}>
                <h3 style={styles.regionTitle}>Costa Almeria</h3>
                <p style={styles.regionSubtitle}>{t('home.costaAlmeriaSubtitle')}</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Float */}
      <a
        href="https://wa.me/31644879415"
        target="_blank"
        rel="noopener noreferrer"
        style={styles.whatsappFloat}
        data-testid="whatsapp-float-button"
      >
        <MessageCircle size={28} />
      </a>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
  },
  hero: {
    position: 'relative',
    height: '90vh',
    minHeight: '600px',
    backgroundImage: 'url(https://images.unsplash.com/photo-1743112412074-107329677542?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGV4dGVyaW9yJTIwc3BhaW4lMjBwb29sJTIwc3Vubnl8ZW58MHx8fHwxNzc0NDMzMDc4fDA&ixlib=rb-4.1.0&q=85)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(26, 25, 24, 0.4)',
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
    color: 'white',
    maxWidth: '900px',
    padding: '0 1.5rem',
  },
  heroTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2.5rem, 6vw, 5rem)',
    lineHeight: 1.1,
    marginBottom: '1.5rem',
    fontWeight: 400,
  },
  heroSubtitle: {
    fontSize: 'clamp(1rem, 2vw, 1.25rem)',
    lineHeight: 1.6,
    marginBottom: '3rem',
    fontWeight: 300,
  },
  heroCTAs: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    fontSize: '0.875rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: 'white',
    border: '1px solid white',
    padding: '1rem 2rem',
    fontSize: '0.875rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
  },
  section: {
    padding: '6rem 0',
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 1.5rem',
  },
  uspGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '2rem',
  },
  uspCard: {
    padding: '2.5rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    textAlign: 'center',
    transition: 'transform 0.3s ease',
  },
  uspTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem',
    color: '#1A1918',
    margin: '1.5rem 0 1rem',
  },
  uspText: {
    color: '#585755',
    fontSize: '0.9rem',
    lineHeight: 1.7,
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '4rem',
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    color: '#1A1918',
    marginBottom: '1rem',
  },
  sectionSubtitle: {
    color: '#8C8B88',
    fontSize: '1.1rem',
  },
  propertiesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem',
  },
  loadingText: {
    textAlign: 'center',
    color: '#8C8B88',
    padding: '3rem 0',
  },
  emptyText: {
    textAlign: 'center',
    color: '#8C8B88',
    padding: '3rem 0',
  },
  ctaCenter: {
    textAlign: 'center',
  },
  regionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
  },
  regionCard: {
    position: 'relative',
    height: '350px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    textDecoration: 'none',
    overflow: 'hidden',
    border: '1px solid #EAE5DD',
    backgroundColor: '#E6DCCA',
  },
  regionOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(197, 160, 89, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'background-color 0.3s ease',
  },
  regionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '2rem',
    color: 'white',
    marginBottom: '0.5rem',
  },
  regionSubtitle: {
    color: 'white',
    fontSize: '1rem',
    fontWeight: 300,
  },
  whatsappFloat: {
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    width: '56px',
    height: '56px',
    backgroundColor: '#25D366',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    textDecoration: 'none',
  },
};

export default Home;
